import type { DatedPoint } from './types'

export type CorrelationPairs = Record<string, DatedPoint[] | number[] | undefined>

export type CorrelationMatrix = {
    keys: string[]
    matrix: number[][]
}

const MIN_PEARSON_SAMPLES = 5

function extractPrices(raw: DatedPoint[] | number[] | undefined): number[] {
    if (!Array.isArray(raw)) return []
    const out: number[] = []
    for (const entry of raw) {
        if (typeof entry === 'number') {
            if (Number.isFinite(entry) && entry > 0) out.push(entry)
            continue
        }
        if (entry && typeof entry === 'object') {
            const y = entry.y
            if (typeof y === 'number' && Number.isFinite(y) && y > 0) out.push(y)
        }
    }
    return out
}

function logReturns(prices: number[]): number[] {
    if (prices.length < 2) return []
    const ret: number[] = new Array(prices.length - 1)
    for (let i = 1; i < prices.length; i++) {
        ret[i - 1] = Math.log(prices[i] / prices[i - 1])
    }
    return ret
}

function pearson(x: number[], y: number[]): number {
    const len = Math.min(x.length, y.length)
    if (len < MIN_PEARSON_SAMPLES) return Number.NaN
    const xStart = x.length - len
    const yStart = y.length - len

    let sx = 0
    let sy = 0
    let count = 0
    for (let i = 0; i < len; i++) {
        const xi = x[xStart + i]
        const yi = y[yStart + i]
        if (Number.isFinite(xi) && Number.isFinite(yi)) {
            sx += xi
            sy += yi
            count++
        }
    }
    if (count < MIN_PEARSON_SAMPLES) return Number.NaN
    const mx = sx / count
    const my = sy / count

    let num = 0
    let dxs = 0
    let dys = 0
    for (let i = 0; i < len; i++) {
        const xi = x[xStart + i]
        const yi = y[yStart + i]
        if (!Number.isFinite(xi) || !Number.isFinite(yi)) continue
        const dx = xi - mx
        const dy = yi - my
        num += dx * dy
        dxs += dx * dx
        dys += dy * dy
    }
    const denom = Math.sqrt(dxs * dys)
    if (!(denom > 0)) return Number.NaN
    return num / denom
}

export function buildCorrelationMatrix(
    pairs: CorrelationPairs,
    order: string[],
): CorrelationMatrix {
    const returnsByKey: Record<string, number[]> = {}
    const keys: string[] = []
    for (const k of order) {
        const prices = extractPrices(pairs[k])
        const ret = logReturns(prices)
        if (ret.length < MIN_PEARSON_SAMPLES) continue
        returnsByKey[k] = ret
        keys.push(k)
    }
    if (keys.length === 0) return { keys: [], matrix: [] }

    const n = keys.length
    const matrix: number[][] = Array.from({ length: n }, () => new Array(n).fill(Number.NaN))
    for (let i = 0; i < n; i++) {
        matrix[i][i] = 1
        for (let j = i + 1; j < n; j++) {
            const r = pearson(returnsByKey[keys[i]], returnsByKey[keys[j]])
            matrix[i][j] = r
            matrix[j][i] = r
        }
    }
    return { keys, matrix }
}
